import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { container } from '@/app/container';
import { SalesRepositoryToken } from '../../di/tokens';
import type { ListSaleWebhookInboxInput } from '../../data/datasources/sales-api.datasource';
import type { ForceProcessSaleWebhookInboxInput } from '../../domain/entities/sales.entity';

const WEBHOOK_INBOX_QUERY_KEY = ['sales', 'webhook-inbox'];

/**
 * Log de webhook inbox (tela admin inspirada no log da Hotmart), filtrado no
 * servidor. `dataUpdatedAt`/`refetch` (do retorno do `useQuery`) alimentam o
 * texto de "última atualização" e o botão de refresh manual da página.
 */
export function useSaleWebhookInbox(input: ListSaleWebhookInboxInput) {
  const repository = container.resolve(SalesRepositoryToken);
  return useQuery({
    queryKey: [
      ...WEBHOOK_INBOX_QUERY_KEY,
      input.page ?? 1,
      input.perPage ?? 10,
      input.status ?? '',
      input.eventType ?? '',
      input.providerAccountId ?? '',
      input.idempotenceSource ?? '',
      input.from ?? '',
      input.to ?? '',
    ],
    queryFn: () => repository.listWebhookInbox(input),
    staleTime: 1000 * 30,
    placeholderData: keepPreviousData,
  });
}

/** Uma row, para o modal "Webhook de origem". Só dispara com o modal aberto. */
export function useSaleWebhookInboxRow(id: string, enabled: boolean) {
  const repository = container.resolve(SalesRepositoryToken);
  return useQuery({
    queryKey: [...WEBHOOK_INBOX_QUERY_KEY, 'row', id],
    queryFn: () => repository.getWebhookInboxRow(id),
    enabled: !!id && enabled,
    staleTime: 1000 * 30,
    retry: false,
  });
}

export function useForceProcessSaleWebhookInbox() {
  const repository = container.resolve(SalesRepositoryToken);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      id: string;
      body: ForceProcessSaleWebhookInboxInput;
    }) => repository.forceProcessWebhookInbox(vars.id, vars.body),
    onSuccess: () =>
      void queryClient.invalidateQueries({
        queryKey: WEBHOOK_INBOX_QUERY_KEY,
      }),
  });
}
